/**
 * UndoManager - Standardized undoable action patterns
 * 
 * Provides utilities to standardize the capture-execute-record pattern
 * used throughout the app for undo/redo functionality.
 * 
 * Usage:
 *   import { withUndo, createRestoreFunctions } from './UndoManager.js';
 *   
 *   // Simple pattern: capture state, execute, record
 *   withUndo({
 *       type: 'delete-device',
 *       description: 'Delete device',
 *       capture: () => ({ device: deepClone(device), connections: [...] }),
 *       execute: () => { deleteDevice(); },
 *       restore: (state) => { addDevice(state.device); ... }
 *   });
 */

import { HISTORY_MAX_SIZE } from '../constants/index.js';
import { eventBus, EVENTS } from './EventBus.js';

// ============= HISTORY SERVICE (INTERNAL) =============

/**
 * HistoryService - Undo/Redo history management
 * 
 * Manages a stack of actions that can be undone and redone.
 * Each action has: { type, description, undo(), redo() }
 */
class HistoryService {
    constructor() {
        this.historyStack = [];  // Actions that can be undone
        this.redoStack = [];     // Actions that can be redone
        this.onUpdate = null;    // Callback when stacks change
    }

    /**
     * Record an action for undo/redo
     * @param {string} type - Action type (e.g., 'add-device')
     * @param {string} description - Human-readable description
     * @param {Function} undoFn - Function to undo the action
     * @param {Function} redoFn - Function to redo the action
     */
    recordAction(type, description, undoFn, redoFn) {
        // Clear redo stack on new action
        this.redoStack.length = 0;
        
        // Add to history stack
        this.historyStack.push({
            type,
            description,
            undo: undoFn,
            redo: redoFn
        });
        
        // Trim if exceeds max size
        while (this.historyStack.length > HISTORY_MAX_SIZE) {
            this.historyStack.shift();
        }
        
        this._notifyUpdate();
    }

    /**
     * Perform undo
     * @returns {{ success: boolean, description?: string, error?: string }}
     */
    undo() {
        if (this.historyStack.length === 0) {
            return { success: false, error: 'Nothing to undo' };
        }
        
        const action = this.historyStack.pop();
        
        try {
            action.undo();
            this.redoStack.push(action);
            this._notifyUpdate();
            return { success: true, description: action.description };
        } catch (err) {
            console.error('Undo failed:', err);
            return { success: false, error: 'Undo failed' };
        }
    }

    /**
     * Perform redo
     * @returns {{ success: boolean, description?: string, error?: string }}
     */
    redo() {
        if (this.redoStack.length === 0) {
            return { success: false, error: 'Nothing to redo' };
        }
        
        const action = this.redoStack.pop();
        
        try {
            action.redo();
            this.historyStack.push(action);
            this._notifyUpdate();
            return { success: true, description: action.description };
        } catch (err) {
            console.error('Redo failed:', err);
            return { success: false, error: 'Redo failed' };
        }
    }

    /**
     * Clear all history
     */
    clear() {
        this.historyStack.length = 0;
        this.redoStack.length = 0;
        this._notifyUpdate();
    }

    /**
     * Check if undo is available
     */
    canUndo() {
        return this.historyStack.length > 0;
    }

    /**
     * Check if redo is available
     */
    canRedo() {
        return this.redoStack.length > 0;
    }

    /**
     * Get the description of the last undoable action
     */
    getUndoDescription() {
        if (this.historyStack.length === 0) return null;
        return this.historyStack[this.historyStack.length - 1].description;
    }

    /**
     * Get the description of the last redoable action
     */
    getRedoDescription() {
        if (this.redoStack.length === 0) return null;
        return this.redoStack[this.redoStack.length - 1].description;
    }

    /**
     * Set a callback to be called when history changes
     * @param {Function} callback 
     */
    setUpdateCallback(callback) {
        this.onUpdate = callback;
    }

    _notifyUpdate() {
        if (this.onUpdate) {
            this.onUpdate();
        }
    }
}

// Singleton instance
const historyService = new HistoryService();

// ============= UNDO MANAGER =============

// Module dependencies (injected via init)
let deps = null;

/**
 * Initialize UndoManager with app dependencies
 * @param {Object} dependencies - App dependencies for restore operations
 */
export function initUndoManager(dependencies) {
    deps = dependencies;
}

/**
 * Get dependencies (for internal use and testing)
 */
export function getDeps() {
    return deps;
}

// ============= STANDARDIZED UNDO PATTERNS =============

/**
 * Execute an action with automatic undo/redo recording.
 * 
 * This is the core helper for standardizing undoable operations.
 * It captures state before execution, runs the action, captures state
 * after (if needed), and records undo/redo functions.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.type - Action type identifier (e.g., 'delete-device')
 * @param {string} config.description - Human-readable description for UI
 * @param {Function} config.capture - Function that returns state to capture before execution
 * @param {Function} config.execute - Function that performs the action (can return after-state)
 * @param {Function} config.undo - Function that takes captured state and undoes the action
 * @param {Function} [config.redo] - Optional custom redo function. If not provided, 
 *                                   execute() is called with after-state
 * @param {boolean} [config.skipHistory=false] - If true, don't record to history
 * @returns {*} Result of execute()
 */
export function withUndo(config) {
    const { type, description, capture, execute, undo, redo, skipHistory = false } = config;
    
    // Capture state before execution
    const beforeState = capture();
    
    // Execute the action
    const result = execute();
    
    // Capture after state if redo needs it (result of execute or separate capture)
    const afterState = result !== undefined ? result : null;
    
    // Record to history unless skipped
    if (!skipHistory) {
        historyService.recordAction(
            type,
            description,
            // Undo function
            () => undo(beforeState),
            // Redo function (use custom or default to re-executing with after state)
            redo ? () => redo(afterState) : () => execute(afterState)
        );
    }
    
    return result;
}

/**
 * Simpler variant: execute action with before/after state capture
 * 
 * Use this when you just need to capture and restore state symmetrically.
 * Both undo and redo simply restore their respective states.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.type - Action type identifier
 * @param {string} config.description - Human-readable description
 * @param {Function} config.getState - Function that returns current state to capture
 * @param {Function} config.setState - Function that restores a captured state
 * @param {Function} config.execute - Function that performs the action
 * @param {boolean} [config.skipHistory=false] - If true, don't record to history
 * @returns {*} Result of execute()
 */
export function withStateUndo(config) {
    const { type, description, getState, setState, execute, skipHistory = false } = config;
    
    // Capture before state
    const beforeState = getState();
    
    // Execute action
    const result = execute();
    
    // Capture after state
    const afterState = getState();
    
    // Record if not skipped
    if (!skipHistory) {
        historyService.recordAction(
            type,
            description,
            () => setState(beforeState),
            () => setState(afterState)
        );
    }
    
    return result;
}

// ============= COMMON RESTORE UTILITIES =============

/**
 * Standard post-action render/save sequence
 * Call after any undoable action to update UI
 */
export function renderAfterUndoAction(options = {}) {
    if (!deps) {
        console.warn('UndoManager: dependencies not initialized');
        return;
    }
    
    const { 
        renderCables = true, 
        updatePorts = true, 
        renderLibrary = false,
        autosave = true 
    } = options;
    
    if (renderCables && deps.renderAllCables) {
        deps.renderAllCables();
    }
    if (updatePorts && deps.updateConnectedPorts) {
        deps.updateConnectedPorts();
    }
    if (renderLibrary && deps.renderLibrary) {
        deps.renderLibrary();
    }
    if (autosave && deps.scheduleAutosave) {
        deps.scheduleAutosave();
    }
}

/**
 * Create standard undo/redo functions for device operations
 * 
 * @param {Object} capturedDevice - Deep clone of device state
 * @param {Array} capturedConnections - Deep clone of device's connections
 * @returns {Object} { undo, redo } functions
 */
export function createDeviceRestoreFunctions(capturedDevice, capturedConnections = []) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { 
        instanceModel, 
        connectionModel, 
        renderDevice, 
        selectionState,
        updateSelectionVisuals
    } = deps;
    
    return {
        /**
         * Restore a deleted device and its connections
         */
        restoreDevice: () => {
            instanceModel.add(capturedDevice);
            renderDevice(capturedDevice);
            capturedConnections.forEach(conn => {
                connectionModel.add(conn);
            });
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - subscribers handle rendering
            // DEVICE_ADDED → LibraryController updates counts
            eventBus.emit(EVENTS.DEVICE_ADDED, { device: capturedDevice, deviceId: capturedDevice.id });
            // CONNECTION_ADDED → CableRenderer renders cables
            capturedConnections.forEach(conn => {
                eventBus.emit(EVENTS.CONNECTION_ADDED, { connection: conn });
            });
        },
        
        /**
         * Remove a device that was added (reverse of restore)
         */
        removeDevice: () => {
            connectionModel.removeByDeviceId(capturedDevice.id);
            instanceModel.remove(capturedDevice.id);
            document.getElementById(capturedDevice.id)?.remove();
            if (selectionState?.devices) {
                selectionState.devices.delete(capturedDevice.id);
            }
            if (updateSelectionVisuals) {
                updateSelectionVisuals();
            }
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit event - CableRenderer + LibraryController handle rendering
            eventBus.emit(EVENTS.DEVICE_DELETED, { deviceId: capturedDevice.id });
        }
    };
}

/**
 * Create standard undo/redo functions for connection operations
 * 
 * @param {Object} capturedConnection - Deep clone of connection
 * @param {Array} replacedConnections - Connections that were replaced (for fan-in/out)
 * @returns {Object} { undo, redo } functions
 */
export function createConnectionRestoreFunctions(capturedConnection, replacedConnections = []) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { connectionModel } = deps;
    
    return {
        /**
         * Restore: remove new connection, bring back replaced ones
         */
        undoConnection: () => {
            connectionModel.remove(capturedConnection.id);
            replacedConnections.forEach(conn => {
                connectionModel.add(conn);
            });
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - CableRenderer handles rendering
            eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId: capturedConnection.id });
            replacedConnections.forEach(conn => {
                eventBus.emit(EVENTS.CONNECTION_ADDED, { connection: conn });
            });
        },
        
        /**
         * Redo: remove replaced connections, add new one
         */
        redoConnection: () => {
            replacedConnections.forEach(conn => {
                connectionModel.remove(conn.id);
            });
            connectionModel.add(capturedConnection);
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - CableRenderer handles rendering
            replacedConnections.forEach(conn => {
                eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId: conn.id });
            });
            eventBus.emit(EVENTS.CONNECTION_ADDED, { connection: capturedConnection });
        }
    };
}

/**
 * Create standard undo/redo functions for batch connection operations
 * (e.g., clearing routing between devices)
 * 
 * @param {Array} capturedConnections - Array of deep-cloned connections
 * @returns {Object} { restoreConnections, removeConnections } functions
 */
export function createBatchConnectionRestoreFunctions(capturedConnections) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { connectionModel } = deps;
    
    return {
        /**
         * Restore all deleted connections
         */
        restoreConnections: () => {
            capturedConnections.forEach(conn => {
                connectionModel.add(conn);
            });
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - CableRenderer handles rendering
            capturedConnections.forEach(conn => {
                eventBus.emit(EVENTS.CONNECTION_ADDED, { connection: conn });
            });
        },
        
        /**
         * Remove all connections (for redo of batch delete)
         */
        removeConnections: () => {
            capturedConnections.forEach(conn => {
                connectionModel.remove(conn.id);
            });
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - CableRenderer handles rendering
            capturedConnections.forEach(conn => {
                eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId: conn.id });
            });
        }
    };
}

/**
 * Create standard undo/redo functions for multi-device operations
 * 
 * @param {Array} capturedDevices - Array of deep-cloned devices
 * @param {Array} capturedConnections - Array of deep-cloned connections
 * @returns {Object} { restoreAll, removeAll } functions
 */
export function createMultiDeviceRestoreFunctions(capturedDevices, capturedConnections = []) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { 
        instanceModel, 
        connectionModel, 
        renderDevice, 
        selectionState,
        clearDeviceSelection
    } = deps;
    
    return {
        /**
         * Restore all deleted devices and connections
         */
        restoreAll: () => {
            capturedDevices.forEach(device => {
                instanceModel.add(device);
                renderDevice(device);
            });
            capturedConnections.forEach(conn => {
                connectionModel.add(conn);
            });
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - subscribers handle rendering
            capturedDevices.forEach(device => {
                eventBus.emit(EVENTS.DEVICE_ADDED, { device, deviceId: device.id });
            });
            capturedConnections.forEach(conn => {
                eventBus.emit(EVENTS.CONNECTION_ADDED, { connection: conn });
            });
        },
        
        /**
         * Remove all devices (for redo of delete)
         */
        removeAll: () => {
            capturedDevices.forEach(device => {
                connectionModel.removeByDeviceId(device.id);
                instanceModel.remove(device.id);
                document.getElementById(device.id)?.remove();
            });
            if (clearDeviceSelection) {
                clearDeviceSelection();
            }
            if (deps.scheduleAutosave) deps.scheduleAutosave();
            
            // Emit events - subscribers handle rendering
            capturedDevices.forEach(device => {
                eventBus.emit(EVENTS.DEVICE_DELETED, { deviceId: device.id });
            });
        }
    };
}

/**
 * Create standard undo/redo functions for position changes
 * 
 * @param {Object} originalPositions - Map of deviceId -> { x, y }
 * @param {Object} finalPositions - Map of deviceId -> { x, y }
 * @returns {Object} { undoMove, redoMove } functions
 */
export function createPositionRestoreFunctions(originalPositions, finalPositions) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { instanceModel } = deps;
    
    const applyPositions = (positions) => {
        Object.entries(positions).forEach(([id, pos]) => {
            const device = instanceModel.getById(id);
            if (device && pos) {
                device.position.x = pos.x;
                device.position.y = pos.y;
                const el = document.getElementById(id);
                if (el) {
                    el.style.left = `${pos.x}px`;
                    el.style.top = `${pos.y}px`;
                }
            }
        });
        renderAfterUndoAction({ updatePorts: false });
    };
    
    return {
        undoMove: () => applyPositions(originalPositions),
        redoMove: () => applyPositions(finalPositions)
    };
}

// ============= SPECIALIZED UNDOABLE ACTIONS =============

/**
 * Delete a device with automatic undo recording
 * 
 * @param {string} deviceId - Device to delete
 * @param {Object} options - Options
 * @param {boolean} options.skipHistory - Don't record to history
 * @returns {boolean} Success
 */
export function deleteDeviceWithUndo(deviceId, options = {}) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { instanceModel, connectionModel, definitionModel, deepClone, showToast } = deps;
    
    const device = instanceModel.getById(deviceId);
    if (!device) return false;
    
    // Capture state
    const deviceCopy = deepClone(device);
    const template = definitionModel.getForDevice(device);
    const deviceName = template ? template.name : 'device';
    const connectionsCopy = connectionModel.getByDeviceId(deviceId).map(c => deepClone(c));
    
    // Create restore functions
    const { restoreDevice, removeDevice } = createDeviceRestoreFunctions(deviceCopy, connectionsCopy);
    
    // Execute deletion
    connectionModel.removeByDeviceId(deviceId);
    instanceModel.remove(deviceId);
    document.getElementById(deviceId)?.remove();
    
    if (deps.selectionState?.devices) {
        deps.selectionState.devices.delete(deviceId);
    }
    if (deps.updateSelectionVisuals) {
        deps.updateSelectionVisuals();
    }
    if (deps.updateSelectionButtons) {
        deps.updateSelectionButtons();
    }
    if (deps.scheduleAutosave) deps.scheduleAutosave();
    
    // Emit event - CableRenderer + LibraryController handle rendering
    eventBus.emit(EVENTS.DEVICE_DELETED, { deviceId });
    
    // Record to history
    if (!options.skipHistory) {
        historyService.recordAction(
            'delete-device',
            `Delete ${deviceName}`,
            restoreDevice,
            removeDevice
        );
    }
    
    if (showToast) {
        showToast('Device deleted');
    }
    
    return true;
}

/**
 * Delete a connection with automatic undo recording
 * 
 * @param {string} connectionId - Connection to delete
 * @param {Object} options - Options
 * @param {boolean} options.skipHistory - Don't record to history
 * @returns {boolean} Success
 */
export function deleteConnectionWithUndo(connectionId, options = {}) {
    if (!deps) {
        throw new Error('UndoManager: dependencies not initialized');
    }
    
    const { connectionModel, deepClone, showToast } = deps;
    
    const connection = connectionModel.getById(connectionId);
    if (!connection) return false;
    
    // Capture state
    const connectionCopy = deepClone(connection);
    
    // Create restore functions
    const { undoConnection } = createConnectionRestoreFunctions(connectionCopy);
    
    // Execute deletion
    connectionModel.remove(connectionId);
    
    if (deps.state) {
        deps.state.selectedConnectionId = null;
    }
    if (deps.updateSelectionButtons) {
        deps.updateSelectionButtons();
    }
    if (deps.scheduleAutosave) deps.scheduleAutosave();
    
    // Emit event - CableRenderer handles rendering
    eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId });
    
    // Record to history
    if (!options.skipHistory) {
        historyService.recordAction(
            'delete-connection',
            'Delete connection',
            undoConnection,
            () => {
                connectionModel.remove(connectionCopy.id);
                if (deps.scheduleAutosave) deps.scheduleAutosave();
                eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId: connectionCopy.id });
            }
        );
    }
    
    if (showToast) {
        showToast('Connection deleted');
    }
    
    return true;
}

// ============= EXPORTS =============

export {
    historyService
};
